import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';

import {
   BadgeListItem,
   BottomMargin,
   DescriptionSpan,
   LightHeadline,
} from '../../../../styledComponents/styledComponents';

import { PropTypes } from 'prop-types';

import { formatTimeNumberInt } from '../../../../util/NumberFormatter';
import { SIMPLE_DATE_FORMAT, SIMPLE_TIME_FORMAT } from '../../../../constants/general';

import BootstrapTable from 'react-bootstrap-table-next';
import storePropTypes from '../../../../redux/store/propTypes';
import Modal from '../../../common/Modal/Modal';
import { DetailTableBody } from '../../../../styledComponents/ModalStyledComponents';
import IpBlockPanel from './IpBlockPanel';
import TokenRevokeRow from './TokenRevokeRow';
import useRevokedTokenFetcher from '../../../../hooks/useRevokedTokenFetcher';
import * as browserStorage from '../../../../misc/BrowserStorageManager';

const columns = [
   {
      text: 'Stream',
      dataField: 'streamName',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'Duration/Sum',
      dataField: 'durationSum',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: formatTimeNumberInt,
   },
   {
      text: 'Duration/Avg',
      dataField: 'durationAvg',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: formatTimeNumberInt,
   },
   {
      text: 'Sessions',
      dataField: 'sessionCount',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'Tokens',
      dataField: 'tokenCount',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'First stream start',
      dataField: 'streamStart',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
   },
   {
      text: 'Last stream end',
      dataField: 'streamEnd',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
   },
];

function IpRowModal({ rowData, closeModal, onBlockAction }) {
   const [copiedBadge, setCopiedBadge] = useState(null);
   const copyTimerRef = useRef(null);

   const { fetchRevokedTokens, revokedMap } = useRevokedTokenFetcher();
   const { isAdmin } = browserStorage.getUserInfo();

   useEffect(() => () => clearTimeout(copyTimerRef.current), []);

   useEffect(() => {
      if (rowData.topTokens.length > 0) {
         fetchRevokedTokens(
            rowData.topTokens.map((jti) =>
               isAdmin ? { jti, orga: rowData.orgaHash } : { jti },
            ),
         );
      }
   }, [fetchRevokedTokens, rowData.topTokens, isAdmin, rowData.orgaHash]);

   const handleCopyBadge = async (value) => {
      await navigator.clipboard.writeText(value).catch(() => {});
      setCopiedBadge(value);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedBadge(null), 1500);
   };

   return (
      <Modal closeModal={closeModal} hint='Click a badge to copy its value'>
         <DetailTableBody>
            <BottomMargin pxSize={20} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
               <DescriptionSpan fontSizePX={20} marginLeftPx={0}>
                  {'Additional details for IP'}
               </DescriptionSpan>
               <BadgeListItem
                  $fontSizePx={14}
                  $cursor={'pointer'}
                  onClick={() => handleCopyBadge(rowData.ip)}
                  className='vertical-padding'
               >
                  {copiedBadge === rowData.ip ? `✓ ${rowData.ip}` : rowData.ip}
               </BadgeListItem>
            </BottomMargin>
            <IpBlockPanel ip={rowData.ip} onBlockAction={onBlockAction} />
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top playback tokens JTIs: ${
                  rowData.topTokens.length === 0 ? ' -' : ''
               }`}</LightHeadline>
               {rowData.topTokens.map((jti) => (
                  <TokenRevokeRow
                     key={jti}
                     jti={jti}
                     from={rowData.streamStart}
                     to={rowData.streamEnd}
                     revokedMap={revokedMap}
                     onActionSuccess={() =>
                        fetchRevokedTokens(
                           rowData.topTokens.map((jti) =>
                              isAdmin ? { jti, orga: rowData.orgaHash } : { jti },
                           ),
                        )
                     }
                  />
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top providers: ${rowData.topProviders.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topProviders.map((provider) => (
                  <BadgeListItem
                     key={provider}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(provider)}
                     className='vertical-padding'
                  >
                     {copiedBadge === provider ? `✓ ${provider}` : provider}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top countries: ${rowData.topCountries.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topCountries.map((country) => (
                  <BadgeListItem
                     key={country}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(country)}
                     className='vertical-padding'
                  >
                     {copiedBadge === country ? `✓ ${country}` : country}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top referrers: ${rowData.topReferrers.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topReferrers.map((referrer) => (
                  <BadgeListItem
                     key={referrer}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(referrer)}
                     className='vertical-padding'
                  >
                     {copiedBadge === referrer ? `✓ ${referrer}` : referrer}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top browser agent ID: ${rowData.topAgents.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topAgents.map((agent) => (
                  <BadgeListItem
                     key={agent}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(agent)}
                     className='vertical-padding'
                  >
                     {copiedBadge === agent ? `✓ ${agent}` : agent}
                  </BadgeListItem>
               ))}
            </BottomMargin>

            <div>
               <BottomMargin pxSize={15}>
                  <LightHeadline $fontSizePx={14} marginBottomPx={10}>
                     Top Streams:
                  </LightHeadline>
                  {rowData.topStreams.map(({ streamName }) => (
                     <BadgeListItem
                        key={streamName}
                        $fontSizePx={13}
                        $cursor={'pointer'}
                        onClick={() => handleCopyBadge(streamName)}
                        className='vertical-padding'
                     >
                        {copiedBadge === streamName ? `✓ ${streamName}` : streamName}
                     </BadgeListItem>
                  ))}
               </BottomMargin>
               <BootstrapTable
                  keyField='streamName'
                  data={rowData.topStreams}
                  columns={columns}
                  classes='table-responsive-lg remove-bottom-margin'
                  id='ExtendedMisuseTable'
                  striped
                  hover
                  bootstrap4
               />
            </div>
         </DetailTableBody>
      </Modal>
   );
}

IpRowModal.propTypes = {
   rowData: storePropTypes.ipMisuseResultRow,
   closeModal: PropTypes.func,
   onBlockAction: PropTypes.func,
};

export default IpRowModal;
